import { useCallback, useEffect, useState } from "react";
import { noteDiagFlash } from "../lib/board/boardDiagTrail";

export type FlashKind = "error" | "ok" | "info";

export type FlashItem = {
  id: number;
  kind: FlashKind;
  text: string;
};

let nextId = 1;

export function useFlashStack(autoHideMs = 6000) {
  const [items, setItems] = useState<FlashItem[]>([]);

  const push = useCallback(
    (kind: FlashKind, text: string) => {
      const id = nextId++;
      noteDiagFlash(kind, text);
      setItems((prev) => [...prev, { id, kind, text }]);
      if (autoHideMs > 0) {
        window.setTimeout(() => {
          setItems((prev) => prev.filter((x) => x.id !== id));
        }, autoHideMs);
      }
    },
    [autoHideMs]
  );

  const dismiss = useCallback((id: number) => {
    setItems((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  return { items, push, dismiss, clear };
}

export function FlashStack({
  items,
  onDismiss,
}: {
  items: FlashItem[];
  onDismiss: (id: number) => void;
}) {
  if (!items.length) return null;
  return (
    <div className="wb-flash-stack" role="status" aria-live="polite">
      {items.map((item) => (
        <FlashBanner key={item.id} item={item} onDismiss={() => onDismiss(item.id)} />
      ))}
    </div>
  );
}

function FlashBanner({ item, onDismiss }: { item: FlashItem; onDismiss: () => void }) {
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") onDismiss();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onDismiss]);

  const cls =
    item.kind === "error"
      ? "wb-alert wb-alert--error"
      : item.kind === "ok"
        ? "wb-alert wb-alert--ok"
        : "wb-alert";

  return (
    <div className={`wb-flash ${cls}`}>
      <span>{item.text}</span>
      <button type="button" className="wb-flash__close" onClick={onDismiss} aria-label="Закрыть">
        ×
      </button>
    </div>
  );
}
